import { inject } from "@angular/core";
import { CanActivateFn, CanMatchFn, Router } from "@angular/router";
import { map } from "rxjs/operators";
import { FeatureFlagService } from "../services/feature-flag.service";
import UserService from "../services/user.service";

/**
 * AB / fallthrough guard (canMatch).
 *
 * Place this on the FIRST of two routes that share the same path. If the flag is
 * off, Angular skips this route and falls through to the next match (e.g. the
 * legacy component). Returns a plain boolean so no redirect occurs.
 *
 * Example:
 *   { path: 'foo', component: FooV2Component, canMatch: [featureFlagMatch('foo-v2')] },
 *   { path: 'foo', component: FooComponent },
 */
export const featureFlagMatch = (flag: string): CanMatchFn => {
    return () => {
        const featureFlagService = inject(FeatureFlagService);
        const userService = inject(UserService);
        // canMatch runs before any component renders, so on direct URL load / HMR the toolbar
        // hasn't called getFeatureFlags() yet — trigger it here to avoid a deadlock.
        userService.getFeatureFlags();
        return featureFlagService.whenReady().pipe(map(() => featureFlagService.isEnabled(flag)));
    };
};

/**
 * Hard-gate guard (canActivate).
 *
 * Use when there is only one version of a route and you want to redirect users
 * who lack the flag. Defaults to redirecting to '/'.
 *
 * Example:
 *   { path: 'beta', component: BetaComponent, canActivate: [featureFlagActivate('beta', '/home')] }
 */
export const featureFlagActivate = (flag: string, redirectTo = "/"): CanActivateFn => {
    return () => {
        const featureFlagService = inject(FeatureFlagService);
        const userService = inject(UserService);
        const router = inject(Router);

        userService.getFeatureFlags();
        return featureFlagService
            .whenReady()
            .pipe(map(() => featureFlagService.isEnabled(flag) || router.createUrlTree([redirectTo])));
    };
};

/**
 * Lazy-route guard (canMatch with redirect).
 *
 * Like featureFlagMatch but designed for lazy-loaded routes where there is no
 * fallback route at the same path. Returning a UrlTree instead of false prevents
 * Angular from downloading the lazy bundle AND sends the user to a known route
 * rather than a 404.
 *
 * Example:
 *   {
 *     path: 'heavy-feature',
 *     loadComponent: () => import('./heavy-feature.component'),
 *     canMatch: [featureFlagLazy('heavy-feature', '/home')],
 *   }
 */
export const featureFlagLazy = (flag: string, redirectTo = "/"): CanMatchFn => {
    return () => {
        const featureFlagService = inject(FeatureFlagService);
        const userService = inject(UserService);
        const router = inject(Router);

        userService.getFeatureFlags();
        return featureFlagService
            .whenReady()
            .pipe(map(() => featureFlagService.isEnabled(flag) || router.createUrlTree([redirectTo])));
    };
};
